import React, { useEffect } from 'react';
import {
    List,
    Datagrid,
    TextField,
    DateField,
    NumberField,
    EditButton,
    DeleteButton,
    Edit,
    SimpleForm,
    ReferenceInput,
    FunctionField,
    FormDataConsumer,
    SelectInput,
    DateInput,
    TextInput,
    NumberInput,
    Create,
    ArrayInput,
    SimpleFormIterator,
    useRecordContext,
    useGetList,
    useInput,
    TopToolbar,
    CreateButton,
    ExportButton,
    FilterButton,
    ListButton,
    Button,
    required,
    useNotify,
    useRefresh,
    useDataProvider,
    usePermissions,
} from 'react-admin';
import AssignmentIcon from '@mui/icons-material/Assignment';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Chip,
    Autocomplete,
    TextField as MuiTextField,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tabs,
    Tab,
    Alert,
    Box,
    Divider,
    Typography,
    List as MuiList,
    ListItem,
    ListItemText,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Grid,
    IconButton,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Checkbox,
    ListItemButton,
    ListItemIcon,
    Paper,
    Button as MuiButton,
    type PaperProps,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useState } from 'react';

export { AssignmentIcon };

const statusColors: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error'> = {
    assigned: 'primary',
    in_progress: 'warning',
    completed: 'success',
    overdue: 'error',
};

const statusLabels: Record<string, string> = {
    assigned: 'Zugewiesen',
    in_progress: 'In Bearbeitung',
    completed: 'Abgeschlossen',
    overdue: 'Überfällig',
};

const StatusChip: React.FC = () => {
    const record = useRecordContext();
    if (!record) return null;
    const status = record.status as string;
    return (
        <Chip
            label={statusLabels[status] ?? status}
            color={statusColors[status] ?? 'default'}
            size="small"
        />
    );
};

// Custom user select that stores the numeric userId (not IRI)
const UserSelectInput: React.FC<{ validate?: any }> = ({ validate }) => {
    const { field, fieldState } = useInput({ source: 'userId', validate });

    const { data: users = [], isLoading } = useGetList('users', {
        pagination: { page: 1, perPage: 500 },
        sort: { field: 'lastName', order: 'ASC' },
        filter: {},
    });

    const options = users.map((u: any) => ({
        id: u.id,
        label: [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || `#${u.id}`,
        email: u.email ?? '',
    }));

    const selected = options.find((o) => o.id === field.value) ?? null;

    return (
        <Autocomplete
            options={options}
            value={selected}
            loading={isLoading}
            onChange={(_, option) => field.onChange(option ? Number(option.id) : null)}
            getOptionLabel={(o) => o.label + (o.email ? ` (${o.email})` : '')}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            fullWidth
            renderInput={(params) => (
                <MuiTextField
                    {...params}
                    label="Benutzer"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {isLoading ? <CircularProgress size={18} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
            sx={{ mb: 2 }}
        />
    );
};

export const assignmentFilters = [
    <ReferenceInput key="course" source="course.id" reference="courses" alwaysOn>
        <SelectInput label="Kurs" optionText="title" />
    </ReferenceInput>,
    <ReferenceInput
        key="learningPath"
        source="learningPath.id"
        reference="learning_paths"
        sort={{ field: 'title', order: 'ASC' }}
        alwaysOn
    >
        <SelectInput label="Lernpfad" optionText="title" />
    </ReferenceInput>,
    <SelectInput
        key="status"
        source="status"
        label="Status"
        choices={[
            { id: 'assigned', name: 'Zugewiesen' },
            { id: 'in_progress', name: 'In Bearbeitung' },
            { id: 'completed', name: 'Abgeschlossen' },
            { id: 'overdue', name: 'Überfällig' },
        ]}
    />,
];

// Shared hook to load users once for list fields
const useUserLookup = () => {
    const { data: users = [] } = useGetList('users', {
        pagination: { page: 1, perPage: 500 },
        sort: { field: 'lastName', order: 'ASC' },
        filter: {},
    });
    return (userId: number) => users.find((u: any) => Number(u.id) === userId);
};

// Show user name in list by looking up from record
const UserNameField: React.FC<{ label?: string; sortBy?: string }> = () => {
    const record = useRecordContext();
    const findUser = useUserLookup();
    if (!record) return null;
    const user = findUser(Number(record.userId));
    if (!user) return <span>User {record.userId}</span>;
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;
    return <span>{name}</span>;
};

// Show user email in list
const UserEmailField: React.FC<{ label?: string }> = () => {
    const record = useRecordContext();
    const findUser = useUserLookup();
    if (!record) return null;
    const user = findUser(Number(record.userId));
    return <span>{user?.email || '–'}</span>;
};

// ─────────────────────────────────────────────────────────────────────────────
// BulkAssignDialog
// ─────────────────────────────────────────────────────────────────────────────

interface ReminderRow {
    daysBeforeDue: number;
    emailTemplateId: number | null;
    recipientType: string;
}

const PaperWithDoneButton = ({ children, onDone, ...props }: PaperProps & { onDone?: () => void }) => (
    <Paper {...props}>
        {children}
        <Box
            sx={{ display: 'flex', justifyContent: 'flex-end', p: 1, borderTop: 1, borderColor: 'divider' }}
            onMouseDown={(e) => e.preventDefault()}
        >
            <MuiButton size="small" variant="contained" onClick={onDone}>
                Fertig
            </MuiButton>
        </Box>
    </Paper>
);

const CheckboxPickList = ({
    label,
    options,
    selectedIds,
    onChange,
    loading,
}: {
    label: string;
    options: { id: number; name: string }[];
    selectedIds: number[];
    onChange: (ids: number[]) => void;
    loading: boolean;
}) => {
    const [query, setQuery] = useState('');
    const needle = query.trim().toLowerCase();
    const visible = needle ? options.filter((o) => o.name.toLowerCase().includes(needle)) : options;
    const toggle = (id: number) =>
        onChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);

    return (
        <Box>
            <MuiTextField
                fullWidth
                size="small"
                label={label}
                placeholder="Suchen..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
            />
            <Box
                sx={{ height: 240, overflow: 'auto', mt: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}
            >
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress size={20} />
                    </Box>
                )}
                {!loading && visible.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                        Keine Treffer
                    </Typography>
                )}
                <MuiList dense disablePadding>
                    {visible.map((option) => (
                        <ListItemButton key={option.id} onClick={() => toggle(option.id)} dense>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                                <Checkbox
                                    edge="start"
                                    size="small"
                                    checked={selectedIds.includes(option.id)}
                                    tabIndex={-1}
                                    disableRipple
                                    inputProps={{ 'aria-label': option.name }}
                                />
                            </ListItemIcon>
                            <ListItemText primary={option.name} />
                        </ListItemButton>
                    ))}
                </MuiList>
            </Box>
            <Typography variant="caption" color="text.secondary">
                {selectedIds.length} von {options.length} ausgewählt
            </Typography>
        </Box>
    );
};

const BulkAssignDialog = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
    const notify = useNotify();
    const refresh = useRefresh();
    const dataProvider = useDataProvider();

    const [targetType, setTargetType] = useState<'course' | 'path'>('course');
    const [courseId, setCourseId] = useState<number | null>(null);
    const [pathId, setPathId] = useState<number | null>(null);
    const [recurrenceWeeks, setRecurrenceWeeks] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [notes, setNotes] = useState('');
    const [tabValue, setTabValue] = useState(0);
    const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
    const [selectedDeptIds, setSelectedDeptIds] = useState<number[]>([]);
    const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);
    const [reminders, setReminders] = useState<ReminderRow[]>([]);
    const [previewResult, setPreviewResult] = useState<any>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [usersOpen, setUsersOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const [courseOptions, setCourseOptions] = useState<any[]>([]);
    const [pathOptions, setPathOptions] = useState<any[]>([]);
    const [userOptions, setUserOptions] = useState<any[]>([]);
    const [deptOptions, setDeptOptions] = useState<any[]>([]);
    const [groupOptions, setGroupOptions] = useState<any[]>([]);
    const [templateOptions, setTemplateOptions] = useState<any[]>([]);
    const [loadingOptions, setLoadingOptions] = useState(false);

    const mainApiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '');
    const token = localStorage.getItem('token');

    // Load courses and templates on open
    useEffect(() => {
        if (!open) return;
        const loadInitial = async () => {
            setLoadingOptions(true);
            try {
                const [coursesRes, pathsRes, templatesRes] = await Promise.all([
                    dataProvider.getList('courses', {
                        pagination: { page: 1, perPage: 500 },
                        sort: { field: 'title', order: 'ASC' },
                        filter: {},
                    }),
                    dataProvider.getList('learning_paths', {
                        pagination: { page: 1, perPage: 500 },
                        sort: { field: 'title', order: 'ASC' },
                        filter: { isActive: true },
                    }),
                    dataProvider.getList('document_templates', {
                        pagination: { page: 1, perPage: 200 },
                        sort: { field: 'name', order: 'ASC' },
                        filter: { type: 'assignment-reminder' },
                    }),
                ]);
                setCourseOptions(coursesRes.data);
                setPathOptions(pathsRes.data);
                setTemplateOptions(templatesRes.data);
                // Load users for first tab
                const usersRes = await dataProvider.getList('users', {
                    pagination: { page: 1, perPage: 200 },
                    sort: { field: 'lastName', order: 'ASC' },
                    filter: {},
                });
                setUserOptions(usersRes.data);
            } catch {
                notify('Fehler beim Laden der Optionen', { type: 'error' });
            } finally {
                setLoadingOptions(false);
            }
        };
        loadInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
        setPreviewResult(null);
        if (newValue === 1 && deptOptions.length === 0) {
            dataProvider
                .getList('departments', {
                    pagination: { page: 1, perPage: 200 },
                    sort: { field: 'name', order: 'ASC' },
                    filter: {},
                })
                .then(({ data }) => setDeptOptions(data))
                .catch(() => notify('Fehler beim Laden der Abteilungen', { type: 'error' }));
        }
        if (newValue === 2 && groupOptions.length === 0) {
            dataProvider
                .getList('user_groups', {
                    pagination: { page: 1, perPage: 200 },
                    sort: { field: 'name', order: 'ASC' },
                    filter: {},
                })
                .then(({ data }) => setGroupOptions(data))
                .catch(() => notify('Fehler beim Laden der Gruppen', { type: 'error' }));
        }
    };

    useEffect(() => {
        const body = {
            userIds: tabValue === 0 ? selectedUserIds : [],
            departmentIds: tabValue === 1 ? selectedDeptIds : [],
            groupIds: tabValue === 2 ? selectedGroupIds : [],
        };
        if (body.userIds.length + body.departmentIds.length + body.groupIds.length === 0) {
            setPreviewResult(null);
            setPreviewLoading(false);
            return;
        }
        let cancelled = false;
        setPreviewLoading(true);
        fetch(`${mainApiUrl}/api/enrollments/preview-users`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(body),
        })
            .then((res) => res.json())
            .then((result) => {
                if (!cancelled) setPreviewResult(result);
            })
            .catch(() => {
                if (!cancelled) notify('Fehler bei der Vorschau', { type: 'error' });
            })
            .finally(() => {
                if (!cancelled) setPreviewLoading(false);
            });
        return () => {
            cancelled = true;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabValue, selectedUserIds, selectedDeptIds, selectedGroupIds]);

    const handleCreate = async () => {
        setLoading(true);
        try {
            const body: any = {
                dueDate,
                notes: notes || null,
                recurrenceWeeks: recurrenceWeeks ? Number(recurrenceWeeks) : null,
                userIds: [],
                departmentIds: [],
                groupIds: [],
                reminders: reminders.map((r) => ({
                    daysBeforeDue: r.daysBeforeDue,
                    emailTemplateId: r.emailTemplateId,
                    recipientType: r.recipientType,
                })),
            };
            if (targetType === 'path') {
                body.pathId = pathId;
            } else {
                body.courseId = courseId;
            }
            if (tabValue === 0) body.userIds = selectedUserIds;
            if (tabValue === 1) body.departmentIds = selectedDeptIds;
            if (tabValue === 2) body.groupIds = selectedGroupIds;

            const res = await fetch(`${mainApiUrl}/api/course-assignments/bulk-create`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(body),
            });
            const result = await res.json();
            if (result.success) {
                notify(`${result.created} erstellt, ${result.skipped} übersprungen`, { type: 'success' });
                refresh();
                handleClose();
            } else {
                notify(result.error || 'Erstellung fehlgeschlagen', { type: 'error' });
            }
        } catch {
            notify('Fehler bei der Erstellung', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setTargetType('course');
        setCourseId(null);
        setPathId(null);
        setRecurrenceWeeks('');
        setDueDate('');
        setNotes('');
        setTabValue(0);
        setSelectedUserIds([]);
        setSelectedDeptIds([]);
        setSelectedGroupIds([]);
        setReminders([]);
        setPreviewResult(null);
        onClose();
    };

    const addReminder = () => {
        setReminders((prev) => [
            ...prev,
            { daysBeforeDue: 3, emailTemplateId: null, recipientType: 'student' },
        ]);
    };

    const removeReminder = (index: number) => {
        setReminders((prev) => prev.filter((_, i) => i !== index));
    };

    const updateReminder = (index: number, field: keyof ReminderRow, value: any) => {
        setReminders((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
    };

    const selectedTargetId = targetType === 'path' ? pathId : courseId;
    const canCreate = !!selectedTargetId && !!dueDate && !!previewResult;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
            <DialogTitle>Massen-Pflichtaufgabe erstellen</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <FormControl fullWidth>
                            <InputLabel>Ziel-Typ *</InputLabel>
                            <Select
                                value={targetType}
                                label="Ziel-Typ *"
                                onChange={(e) => {
                                    setTargetType(e.target.value as 'course' | 'path');
                                    setCourseId(null);
                                    setPathId(null);
                                }}
                            >
                                <MenuItem value="course">Einzelner Kurs</MenuItem>
                                <MenuItem value="path">Lernpfad</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 8 }}>
                        {targetType === 'course' ? (
                            <Autocomplete
                                options={courseOptions}
                                getOptionLabel={(opt: any) => opt.title || `#${opt.id}`}
                                value={courseOptions.find((c: any) => c.id === courseId) ?? null}
                                onChange={(_, option) => {
                                    setCourseId(option ? option.id : null);
                                }}
                                loading={loadingOptions}
                                isOptionEqualToValue={(opt: any, val: any) => opt.id === val.id}
                                renderInput={(params) => (
                                    <MuiTextField
                                        {...params}
                                        label="Kurs *"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {loadingOptions && <CircularProgress size={18} />}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                fullWidth
                            />
                        ) : (
                            <Autocomplete
                                options={pathOptions}
                                getOptionLabel={(opt: any) => opt.title || `#${opt.id}`}
                                value={pathOptions.find((p: any) => p.id === pathId) ?? null}
                                onChange={(_, option) => {
                                    setPathId(option ? option.id : null);
                                }}
                                loading={loadingOptions}
                                isOptionEqualToValue={(opt: any, val: any) => opt.id === val.id}
                                renderInput={(params) => (
                                    <MuiTextField
                                        {...params}
                                        label="Lernpfad *"
                                        helperText="Teilnehmende werden automatisch in alle Pflicht-Kurse des Pfads eingeschrieben."
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {loadingOptions && <CircularProgress size={18} />}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                fullWidth
                            />
                        )}
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <MuiTextField
                            type="date"
                            label="Fälligkeitsdatum *"
                            value={dueDate}
                            onChange={(e) => {
                                setDueDate(e.target.value);
                            }}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <MuiTextField
                            type="number"
                            label="Wiederholung alle … Wochen (optional)"
                            value={recurrenceWeeks}
                            onChange={(e) => setRecurrenceWeeks(e.target.value)}
                            fullWidth
                            inputProps={{ min: 1 }}
                            helperText="Leer = einmalig. Nach Ablauf muss die Aufgabe erneut absolviert werden."
                        />
                    </Grid>
                    <Grid size={12}>
                        <MuiTextField
                            label="Notiz (optional)"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            multiline
                            rows={2}
                            fullWidth
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Benutzer auswählen
                </Typography>

                <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
                    <Tab label="Einzelne Benutzer" />
                    <Tab label="Nach Abteilung" />
                    <Tab label="Nach Gruppe" />
                </Tabs>

                {tabValue === 0 && (
                    <Box>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                                setSelectedUserIds(userOptions.map((u: any) => u.id));
                                setPreviewResult(null);
                            }}
                            disabled={loadingOptions || userOptions.length === 0}
                        >
                            Alle auswählen ({userOptions.length})
                        </Button>
                        {selectedUserIds.length > 0 && (
                            <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                onClick={() => {
                                    setSelectedUserIds([]);
                                    setPreviewResult(null);
                                }}
                            >
                                Auswahl aufheben
                            </Button>
                        )}
                    </Box>
                    <Autocomplete
                        multiple
                        disableCloseOnSelect
                        open={usersOpen}
                        onOpen={() => setUsersOpen(true)}
                        onClose={() => setUsersOpen(false)}
                        slots={{ paper: PaperWithDoneButton }}
                        slotProps={{
                            paper: { onDone: () => setUsersOpen(false) } as PaperProps,
                            listbox: { style: { maxHeight: 240 } },
                        }}
                        options={userOptions}
                        getOptionLabel={(opt: any) =>
                            `${opt.firstName || ''} ${opt.lastName || ''} (${opt.email})`.trim()
                        }
                        value={userOptions.filter((u: any) => selectedUserIds.includes(u.id))}
                        onChange={(_, newValue: any[]) => {
                            setSelectedUserIds(newValue.map((u) => u.id));
                            setPreviewResult(null);
                        }}
                        loading={loadingOptions}
                        isOptionEqualToValue={(opt: any, val: any) => opt.id === val.id}
                        renderInput={(params) => (
                            <MuiTextField
                                {...params}
                                label="Benutzer auswählen"
                                placeholder="Suchen..."
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loadingOptions && <CircularProgress size={18} />}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                    </Box>
                )}

                {tabValue === 1 && (
                    <CheckboxPickList
                        label="Abteilung auswählen"
                        options={deptOptions}
                        selectedIds={selectedDeptIds}
                        onChange={setSelectedDeptIds}
                        loading={loadingOptions}
                    />
                )}

                {tabValue === 2 && (
                    <CheckboxPickList
                        label="Gruppe auswählen"
                        options={groupOptions}
                        selectedIds={selectedGroupIds}
                        onChange={setSelectedGroupIds}
                        loading={loadingOptions}
                    />
                )}

                <Box sx={{ mt: 2 }}>
                    <Alert severity="info" sx={{ mb: 1 }} icon={previewLoading ? <CircularProgress size={18} /> : undefined}>
                        {previewLoading
                            ? 'Benutzer werden geladen …'
                            : previewResult
                              ? `${previewResult.count} Benutzer gefunden`
                              : 'Noch keine Auswahl'}
                    </Alert>
                    <Box
                        sx={{
                            height: 200,
                            overflow: 'auto',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            opacity: previewLoading ? 0.5 : 1,
                        }}
                    >
                        <MuiList dense>
                            {(previewResult?.users ?? []).map((user: any) => (
                                <ListItem key={user.id}>
                                    <ListItemText
                                        primary={`${user.firstName} ${user.lastName}`}
                                        secondary={`${user.email}${user.department ? ` · ${user.department}` : ''}`}
                                    />
                                </ListItem>
                            ))}
                        </MuiList>
                    </Box>
                </Box>

                <Accordion sx={{ mt: 2 }} disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2">
                            Erinnerungen (optional) {reminders.length > 0 && `– ${reminders.length}`}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        {reminders.map((reminder, index) => (
                            <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                                <MuiTextField
                                    label="Tage vor Fälligkeit"
                                    type="number"
                                    value={reminder.daysBeforeDue}
                                    onChange={(e) =>
                                        updateReminder(index, 'daysBeforeDue', parseInt(e.target.value) || 0)
                                    }
                                    sx={{ width: 180 }}
                                    size="small"
                                />
                                <FormControl sx={{ minWidth: 220 }} size="small">
                                    <InputLabel>E-Mail-Vorlage</InputLabel>
                                    <Select
                                        value={reminder.emailTemplateId ?? ''}
                                        label="E-Mail-Vorlage"
                                        onChange={(e) =>
                                            updateReminder(
                                                index,
                                                'emailTemplateId',
                                                String(e.target.value) === '' ? null : Number(e.target.value)
                                            )
                                        }
                                    >
                                        <MenuItem value="">
                                            <em>Keine</em>
                                        </MenuItem>
                                        {templateOptions.map((t: any) => (
                                            <MenuItem key={t.id} value={t.id}>
                                                {t.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl sx={{ minWidth: 160 }} size="small">
                                    <InputLabel>Empfänger</InputLabel>
                                    <Select
                                        value={reminder.recipientType}
                                        label="Empfänger"
                                        onChange={(e) =>
                                            updateReminder(index, 'recipientType', e.target.value)
                                        }
                                    >
                                        <MenuItem value="student">Benutzer</MenuItem>
                                        <MenuItem value="assigned_by">Zuweisende</MenuItem>
                                        <MenuItem value="managers">Admins</MenuItem>
                                    </Select>
                                </FormControl>
                                <IconButton onClick={() => removeReminder(index)} size="small" color="error">
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        ))}
                        <Button
                            onClick={addReminder}
                            label="Erinnerung hinzufügen"
                            startIcon={<AddIcon />}
                            size="small"
                        />
                    </AccordionDetails>
                </Accordion>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} label="Abbrechen" />
                <Button
                    onClick={handleCreate}
                    label="Erstellen"
                    disabled={!canCreate || loading}
                    variant="contained"
                />
            </DialogActions>
        </Dialog>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// List
// ─────────────────────────────────────────────────────────────────────────────

const CourseAssignmentListActions = ({ onBulkOpen }: { onBulkOpen: () => void }) => {
    // Walter-Report 2026-05-26: COMPANY_ADMIN ist die hoechste Tenant-Rolle
    // (Level 70) und darf laut CourseAssignment-Entity-Security ohnehin
    // einzelne Pflichtaufgaben anlegen — Bulk-Assignment muss konsistent
    // erlaubt sein. Backend (BulkCourseAssignmentController) wurde
    // entsprechend symmetrisch geoeffnet.
    const { permissions } = usePermissions();
    const canBulkAssign = Array.isArray(permissions) && (
        permissions.includes('ROLE_INSTRUCTOR') ||
        permissions.includes('ROLE_CONTENT_MANAGER') ||
        permissions.includes('ROLE_COMPANY_ADMIN') ||
        permissions.includes('ROLE_OPERATOR') ||
        permissions.includes('ROLE_PLATFORM_ADMIN')
    );
    return (
        <TopToolbar>
            <FilterButton />
            {canBulkAssign && (
                <Button onClick={onBulkOpen} label="Massen-Zuweisung" startIcon={<GroupAddIcon />} />
            )}
            <CreateButton />
            <ExportButton />
        </TopToolbar>
    );
};

export const CourseAssignmentList: React.FC = () => {
    const [bulkOpen, setBulkOpen] = useState(false);
    return (
        <>
            <List
                actions={<CourseAssignmentListActions onBulkOpen={() => setBulkOpen(true)} />}
                filters={assignmentFilters}
                sort={{ field: 'dueDate', order: 'ASC' }}
                empty={false}
            >
                <Datagrid rowClick="edit" bulkActionButtons={false}>
                    <UserNameField label="Benutzer" sortBy="userId" />
                    <UserEmailField label="E-Mail" />
                    <FunctionField
                        label="Ziel"
                        render={(record: any) => (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip
                                    label={record?.targetType === 'path' ? 'Lernpfad' : 'Kurs'}
                                    color={record?.targetType === 'path' ? 'secondary' : 'default'}
                                    size="small"
                                    variant="outlined"
                                />
                                <span>{record?.targetTitle || ''}</span>
                            </Box>
                        )}
                    />
                    <DateField source="dueDate" label="Fällig bis" />
                    <FunctionField
                        label="Wiederholung"
                        render={(record: any) =>
                            record?.recurrenceWeeks ? `alle ${record.recurrenceWeeks} Wo.` : '–'
                        }
                    />
                    <StatusChip />
                    <NumberField source="reminders.length" label="Erinnerungen" />
                    <EditButton />
                    <DeleteButton />
                </Datagrid>
            </List>
            <BulkAssignDialog open={bulkOpen} onClose={() => setBulkOpen(false)} />
        </>
    );
};

const reminderRecipientChoices = [
    { id: 'student', name: 'Benutzer' },
    { id: 'assigned_by', name: 'Zuweisende' },
    { id: 'managers', name: 'Admins' },
];

const ReminderFields: React.FC = () => (
    <ArrayInput source="reminders" label="Erinnerungen">
        {/* Trennlinie zwischen Zeilen + Luft, damit das floatende Outlined-Label der Folgezeile nicht auf der Border klebt. */}
        <SimpleFormIterator inline sx={{ '& .RaSimpleFormIterator-line': { pt: 2 } }}>
            <NumberInput
                source="daysBeforeDue"
                label="Tage vor Fälligkeit (negativ = nach Fälligkeit)"
                defaultValue={3}
                margin="dense"
                sx={{ width: 260 }}
            />
            <ReferenceInput
                source="emailTemplate"
                reference="document_templates"
                filter={{ type: 'assignment-reminder' }}
                label="E-Mail-Vorlage"
            >
                <SelectInput optionText="name" label="E-Mail-Vorlage" sx={{ width: 220 }} />
            </ReferenceInput>
            <SelectInput
                source="recipientType"
                label="Empfänger"
                choices={reminderRecipientChoices}
                defaultValue="student"
                sx={{ width: 180 }}
            />
        </SimpleFormIterator>
    </ArrayInput>
);

const targetTypeChoices = [
    { id: 'course', name: 'Einzelner Kurs' },
    { id: 'path', name: 'Lernpfad' },
];

// Ziel-Auswahl: je nach targetType entweder Kurs- oder Lernpfad-Referenz.
const AssignmentTargetFields: React.FC = () => (
    <>
        <SelectInput
            source="targetType"
            label="Ziel-Typ"
            choices={targetTypeChoices}
            defaultValue="course"
            validate={required()}
            fullWidth
        />
        <FormDataConsumer>
            {({ formData }) =>
                formData.targetType === 'path' ? (
                    <ReferenceInput source="learningPath" reference="learning_paths" label="Lernpfad" sort={{ field: 'title', order: 'ASC' }}>
                        <SelectInput
                            optionText="title"
                            label="Lernpfad"
                            validate={required()}
                            helperText="Teilnehmende werden automatisch in alle Pflicht-Kurse des Pfads eingeschrieben."
                            fullWidth
                        />
                    </ReferenceInput>
                ) : (
                    <ReferenceInput source="course" reference="courses" label="Kurs" sort={{ field: 'title', order: 'ASC' }}>
                        <SelectInput optionText="title" label="Kurs" validate={required()} fullWidth />
                    </ReferenceInput>
                )
            }
        </FormDataConsumer>
    </>
);

const RecurrenceField: React.FC = () => (
    <NumberInput
        source="recurrenceWeeks"
        label="Wiederholung alle … Wochen (optional)"
        min={1}
        helperText="Leer = einmalig. Nach Ablauf muss die Aufgabe erneut absolviert werden."
        fullWidth
    />
);

export const CourseAssignmentCreate: React.FC = () => (
    <Create actions={<TopToolbar><ListButton /></TopToolbar>}>
        <SimpleForm>
            <AssignmentTargetFields />
            <UserSelectInput validate={required()} />
            <DateInput source="dueDate" label="Fälligkeitsdatum" validate={required()} fullWidth InputLabelProps={{ shrink: true }} />
            <RecurrenceField />
            <TextInput source="notes" label="Notiz für den Benutzer (optional)" multiline rows={3} fullWidth />
            <ReminderFields />
        </SimpleForm>
    </Create>
);

export const CourseAssignmentEdit: React.FC = () => (
    <Edit actions={<TopToolbar><ListButton /></TopToolbar>}>
        <SimpleForm>
            <AssignmentTargetFields />
            <UserSelectInput validate={required()} />
            <DateInput source="dueDate" label="Fälligkeitsdatum" validate={required()} fullWidth InputLabelProps={{ shrink: true }} />
            <RecurrenceField />
            <TextInput source="notes" label="Notiz für den Benutzer (optional)" multiline rows={3} fullWidth />
            <ReminderFields />
        </SimpleForm>
    </Edit>
);
